/**
 * ExecutionHistory Component
 *
 * Displays recent automation executions with their status and details.
 * Supports filtering by status and pagination via "Load more" button.
 */

import { memo, useState, useCallback, useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type {
  IExecutionHistoryProps,
  IAutomationExecution,
  IAutomationRule,
  AutomationExecutionStatus,
  AutomationContentType,
} from '@/@types/automation';
import { AutomationStatusBadge } from './AutomationStatusBadge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { formatDistanceToNow, format } from 'date-fns';
import {
  Camera,
  FileText,
  Video,
  ExternalLink,
  RefreshCw,
  Clock,
  ChevronDown,
  AlertCircle,
} from 'lucide-react';

/**
 * Status filter options
 */
type StatusFilter = 'all' | AutomationExecutionStatus;

/**
 * Status filter configuration
 */
const STATUS_FILTER_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'generating', label: 'Generating' },
  { value: 'awaiting_approval', label: 'Awaiting Approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'published', label: 'Published' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
];

/**
 * Get content type icon component
 */
function getContentTypeIcon(contentType: AutomationContentType | undefined) {
  switch (contentType) {
    case 'photo':
      return <Camera className="h-4 w-4" />;
    case 'post':
      return <FileText className="h-4 w-4" />;
    case 'reel':
      return <Video className="h-4 w-4" />;
    default:
      return <FileText className="h-4 w-4" />;
  }
}

/**
 * Map generated content type to automation content type
 */
function mapContentType(
  generatedType: 'aiStudioSession' | 'post' | 'reel' | undefined
): AutomationContentType | undefined {
  if (!generatedType) return undefined;
  if (generatedType === 'aiStudioSession') return 'photo';
  return generatedType;
}

/**
 * Truncate text with ellipsis
 */
function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

/**
 * Loading skeleton for execution history
 */
function ExecutionHistorySkeleton() {
  return (
    <div className="space-y-2">
      {['skeleton-exec-a', 'skeleton-exec-b', 'skeleton-exec-c'].map(key => (
        <Card key={key}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-4">
              <Skeleton className="h-12 w-12 rounded" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-24" />
              </div>
              <Skeleton className="h-6 w-20" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/**
 * Empty state for execution history
 */
function ExecutionHistoryEmpty() {
  return (
    <Card>
      <CardHeader className="text-center">
        <Clock className="mx-auto h-12 w-12 text-muted-foreground" />
        <CardTitle>No Executions Yet</CardTitle>
        <CardDescription>
          Automation executions will appear here once rules start running
        </CardDescription>
      </CardHeader>
    </Card>
  );
}

/**
 * Single execution item component
 */
interface IExecutionItemProps {
  execution: IAutomationExecution;
  ruleName: string | undefined;
  contentType: AutomationContentType | undefined;
  onView?: (executionId: IAutomationExecution['_id']) => void;
  onRetry?: (executionId: IAutomationExecution['_id']) => void;
}

const ExecutionItem = memo(function ExecutionItem({
  execution,
  ruleName,
  contentType,
  onView,
  onRetry,
}: IExecutionItemProps) {
  const handleView = useCallback(() => {
    onView?.(execution._id);
  }, [onView, execution._id]);

  const handleRetry = useCallback(() => {
    onRetry?.(execution._id);
  }, [onRetry, execution._id]);

  const effectiveContentType = contentType ?? mapContentType(execution.generatedContentType);

  return (
    <Card className="transition-colors hover:border-primary/30">
      <CardContent className="pt-4">
        <div className="flex items-start gap-4">
          {/* Content type icon */}
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
            {getContentTypeIcon(effectiveContentType)}
          </div>

          {/* Main content */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium">{ruleName ?? 'Unknown Rule'}</span>
              <AutomationStatusBadge status={execution.status} />
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
              <span title={format(new Date(execution.scheduledFor), 'PPpp')}>
                {formatDistanceToNow(new Date(execution.scheduledFor), { addSuffix: true })}
              </span>
              {execution.status === 'failed' && execution.errorMessage && (
                <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
                  <AlertCircle className="h-3 w-3" />
                  {truncateText(execution.errorMessage, 50)}
                </span>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex shrink-0 items-center gap-2">
            {execution.status === 'failed' && onRetry && (
              <Button variant="ghost" size="sm" onClick={handleRetry} className="gap-1">
                <RefreshCw className="h-3.5 w-3.5" />
                Retry
              </Button>
            )}
            {onView && (
              <Button variant="ghost" size="sm" onClick={handleView} className="gap-1">
                <ExternalLink className="h-3.5 w-3.5" />
                View
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
});

export const ExecutionHistory = memo(function ExecutionHistory({
  ruleId,
  limit = 20,
  showViewAll = false,
  className,
}: IExecutionHistoryProps) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [displayLimit, setDisplayLimit] = useState(limit);

  // Fetch executions
  const executions = useQuery(api.automation.executions.list, {
    ruleId,
    limit: displayLimit,
  });

  // Fetch rules to get names
  const rules = useQuery(api.automation.rules.list, {});

  const isLoading = executions === undefined || rules === undefined;

  // Build rule lookup map
  const ruleMap = useMemo((): Map<string, IAutomationRule> => {
    if (!rules) return new Map<string, IAutomationRule>();
    return new Map(rules.map((rule: IAutomationRule) => [rule._id as string, rule]));
  }, [rules]);

  // Filter executions by status
  const filteredExecutions = useMemo((): IAutomationExecution[] => {
    if (!executions) return [];
    if (statusFilter === 'all') return executions as IAutomationExecution[];
    return (executions as IAutomationExecution[]).filter(
      (e: IAutomationExecution) => e.status === statusFilter
    );
  }, [executions, statusFilter]);

  const handleStatusFilterChange = useCallback((value: string) => {
    setStatusFilter(value as StatusFilter);
  }, []);

  const handleLoadMore = useCallback(() => {
    setDisplayLimit(prev => prev + 20);
  }, []);

  // Handle view/retry callbacks (placeholder - would be implemented with dialogs)
  const handleView = useCallback((_executionId: IAutomationExecution['_id']) => {
    // TODO: Open execution detail dialog
  }, []);

  const handleRetry = useCallback((_executionId: IAutomationExecution['_id']) => {
    // TODO: Trigger retry mutation
  }, []);

  if (isLoading) {
    return (
      <div className={cn('space-y-4', className)}>
        <ExecutionHistorySkeleton />
      </div>
    );
  }

  if (filteredExecutions.length === 0 && statusFilter === 'all') {
    return (
      <div className={cn('space-y-4', className)}>
        <ExecutionHistoryEmpty />
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Filter header */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {filteredExecutions.length} execution{filteredExecutions.length !== 1 ? 's' : ''}
        </div>
        <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_FILTER_OPTIONS.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Execution list */}
      {filteredExecutions.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No executions match the selected filter
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filteredExecutions.map((execution: IAutomationExecution) => {
            const rule = ruleMap.get(execution.ruleId as string);
            return (
              <ExecutionItem
                key={execution._id}
                execution={execution}
                ruleName={rule?.name}
                contentType={rule?.contentType}
                onView={handleView}
                onRetry={handleRetry}
              />
            );
          })}
        </div>
      )}

      {/* Load more / View all */}
      <div className="flex justify-center gap-4">
        {executions && executions.length >= displayLimit && (
          <Button variant="outline" onClick={handleLoadMore} className="gap-2">
            <ChevronDown className="h-4 w-4" />
            Load More
          </Button>
        )}
        {showViewAll && (
          <Button variant="ghost" className="gap-2">
            View All
            <ExternalLink className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
});
